/**
 * Transaction History View Component
 * Displays user's transaction history with filtering and pagination
 */

import React, { useEffect, useState } from 'react';
import { apiClient, type TransactionsResponse } from '../services/apiClient';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatNumber, formatCurrency, formatDateTime } from '../utils/persian';

type StatusFilter = 'all' | 'completed' | 'pending' | 'failed';

export const TransactionView: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadTransactions(1, statusFilter);
  }, [statusFilter]);

  const loadTransactions = async (pageNum: number, status: StatusFilter) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const statusParam = status === 'all' ? undefined : status;
      const data = await apiClient.getTransactions(pageNum, 20, statusParam);
      
      if (pageNum === 1) {
        setTransactions(data);
      } else {
        // Append to existing transactions
        setTransactions(prev => prev ? {
          ...data,
          transactions: [...prev.transactions, ...data.transactions],
        } : data);
      }
      
      setPage(pageNum);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در بارگذاری تاریخچه تراکنش‌ها';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (transactions && transactions.transactions.length < transactions.total) {
      loadTransactions(page + 1, statusFilter);
    }
  };

  const handleFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    setPage(1);
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    const styles: Record<string, React.CSSProperties> = {
      'completed': { backgroundColor: '#e8f5e9', color: '#2e7d32' },
      'pending': { backgroundColor: '#fff3e0', color: '#f57c00' },
      'failed': { backgroundColor: '#ffebee', color: '#c62828' },
      'cancelled': { backgroundColor: '#f5f5f5', color: '#666' },
    };
    return styles[status.toLowerCase()] || styles['pending'];
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'completed': 'موفق',
      'pending': 'در انتظار',
      'failed': 'ناموفق',
      'cancelled': 'لغو شده',
    };
    return labels[status.toLowerCase()] || status;
  };

  const getStatusIcon = (status: string): string => {
    const icons: Record<string, string> = {
      'completed': '✅',
      'pending': '⏳',
      'failed': '❌',
      'cancelled': '🚫',
    };
    return icons[status.toLowerCase()] || '❓';
  };

  const getTransactionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'subscription': 'خرید اشتراک',
      'top_up': 'شارژ اعتبار',
      'refund': 'بازگشت وجه',
    };
    return labels[type.toLowerCase()] || type;
  };

  const getPaymentMethodLabel = (method: string): string => {
    const labels: Record<string, string> = {
      'zarinpal': 'زرین‌پال',
      'bale_wallet': 'کیف پول بله',
      'manual': 'دستی',
    };
    return labels[method.toLowerCase()] || method;
  };

  if (loading) {
    return <LoadingSpinner message="در حال بارگذاری تاریخچه تراکنش‌ها..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => loadTransactions(1, statusFilter)} />;
  }

  if (!transactions || transactions.transactions.length === 0) {
    return (
      <div style={componentStyles.emptyState}>
        <p style={componentStyles.emptyIcon}>💳</p>
        <p style={componentStyles.emptyText}>هنوز هیچ تراکنشی ثبت نشده است</p>
        <p style={componentStyles.emptySubtext}>
          پس از خرید پلن یا شارژ اعتبار، تاریخچه تراکنش‌های شما اینجا نمایش داده می‌شود.
        </p>
      </div>
    );
  }

  return (
    <div style={componentStyles.container}>
      <div style={componentStyles.header}>
        <h2 style={componentStyles.title}>تاریخچه تراکنش‌ها</h2>
        <p style={componentStyles.subtitle}>
          {formatNumber(transactions.total)} مورد
        </p>
      </div>

      {/* Status Filter */}
      <div style={componentStyles.filterContainer}>
        {(['all', 'completed', 'pending', 'failed'] as StatusFilter[]).map((filter) => (
          <button
            key={filter}
            onClick={() => handleFilterChange(filter)}
            style={{
              ...componentStyles.filterButton,
              ...(statusFilter === filter ? componentStyles.filterButtonActive : {}),
            }}
          >
            {filter === 'all' ? 'همه' : getStatusLabel(filter)}
          </button>
        ))}
      </div>

      {/* Transaction List */}
      <div style={componentStyles.transactionList}>
        {transactions.transactions.map((transaction) => (
          <div key={transaction.id} style={componentStyles.transactionCard}>
            <div style={componentStyles.transactionHeader}>
              <div style={componentStyles.transactionInfo}>
                <p style={componentStyles.transactionType}>
                  {getTransactionTypeLabel(transaction.type)}
                </p>
                <p style={componentStyles.transactionDate}>
                  {formatDateTime(transaction.created_at)}
                </p>
              </div>
              <div style={{
                ...componentStyles.statusBadge,
                ...getStatusStyle(transaction.status),
              }}>
                <span style={componentStyles.statusIcon}>
                  {getStatusIcon(transaction.status)}
                </span>
                <span style={componentStyles.statusText}>
                  {getStatusLabel(transaction.status)}
                </span>
              </div>
            </div>

            <div style={componentStyles.transactionDetails}>
              <div style={componentStyles.detailRow}>
                <span style={componentStyles.detailLabel}>مبلغ:</span>
                <span style={componentStyles.detailValue}>
                  {formatCurrency(transaction.amount_toman)}
                </span>
              </div>

              <div style={componentStyles.detailRow}>
                <span style={componentStyles.detailLabel}>اعتبار دریافتی:</span>
                <span style={componentStyles.detailValue}>
                  +{formatNumber(transaction.credits_added)} واحد
                </span>
              </div>

              <div style={componentStyles.detailRow}>
                <span style={componentStyles.detailLabel}>روش پرداخت:</span>
                <span style={componentStyles.detailValue}>
                  {getPaymentMethodLabel(transaction.payment_method)}
                </span>
              </div>

              {transaction.plan && (
                <div style={componentStyles.detailRow}>
                  <span style={componentStyles.detailLabel}>پلن:</span>
                  <span style={componentStyles.detailValue}>
                    {transaction.plan.name_fa || transaction.plan.name}
                  </span>
                </div>
              )}

              <div style={componentStyles.detailRow}>
                <span style={componentStyles.detailLabel}>شناسه تراکنش:</span>
                <span style={componentStyles.detailValueMono}>
                  {transaction.id.substring(0, 12)}...
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {transactions.transactions.length < transactions.total && (
        <div style={componentStyles.loadMoreContainer}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={componentStyles.loadMoreButton}
          >
            {loadingMore ? 'در حال بارگذاری...' : 'موارد بیشتر'}
          </button>
        </div>
      )}
    </div>
  );
};

const componentStyles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  filterContainer: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    overflowX: 'auto',
    paddingBottom: '0.5rem',
  },
  filterButton: {
    padding: '0.5rem 1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    backgroundColor: '#ffffff',
    color: '#666',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  filterButtonActive: {
    backgroundColor: '#646cff',
    color: 'white',
    borderColor: '#646cff',
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  transactionCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
  },
  transactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
  },
  transactionInfo: {
    flex: 1,
  },
  transactionType: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  transactionDate: {
    fontSize: '0.75rem',
    color: '#999',
    margin: 0,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  statusIcon: {
    fontSize: '0.875rem',
  },
  statusText: {},
  transactionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #f0f0f0',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
  },
  detailLabel: {
    color: '#666',
  },
  detailValue: {
    color: '#1a1a1a',
    fontWeight: 500,
  },
  detailValueMono: {
    color: '#1a1a1a',
    fontWeight: 500,
    fontFamily: 'monospace',
    direction: 'ltr',
    textAlign: 'left',
  },
  loadMoreContainer: {
    marginTop: '1rem',
    textAlign: 'center',
  },
  loadMoreButton: {
    backgroundColor: '#646cff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  emptyState: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '3rem 1.5rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '3rem',
    margin: '0 0 1rem 0',
  },
  emptyText: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.5rem 0',
  },
  emptySubtext: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: 1.6,
  },
};
